import { useMemo, useState, useRef } from "react";
import { Box, IconButton, Popover, TextField, Tooltip, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PushPinIcon from "@mui/icons-material/PushPin";
import { useChatRail } from "./ChatRailProvider";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import {
  useCloseConversation,
  useCreateConversation,
  useRenameConversation,
  type ConversationTab,
} from "../hooks/useConversationTabs";
import { computeVisibleOverflow } from "./conversationTabsLogic";

interface TabChromeProps {
  tab: ConversationTab;
  active: boolean;
  onClick: () => void;
  onClose: () => void;
  onRename: (next: string) => void;
}

function TabChrome({ tab, active, onClick, onClose, onRename }: TabChromeProps) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tab.title ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const startEdit = () => {
    setDraft(tab.title ?? "");
    setEditing(true);
    queueMicrotask(() => inputRef.current?.focus());
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== (tab.title ?? "")) onRename(trimmed);
  };

  return (
    <Box
      role="tab"
      aria-selected={active}
      onClick={onClick}
      onDoubleClick={startEdit}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: T.conversation_tab_height - 4,
        px: 1,
        mx: 0.25,
        gap: 0.5,
        borderTopLeftRadius: T.r_md,
        borderTopRightRadius: T.r_md,
        bgcolor: active ? T.rail : T.panel2,
        borderTop: `1px solid ${active ? T.hair2 : T.hair}`,
        borderLeft: `1px solid ${active ? T.hair2 : T.hair}`,
        borderRight: `1px solid ${active ? T.hair2 : T.hair}`,
        borderBottom: active ? `1px solid ${T.rail}` : `1px solid ${T.hair}`,
        position: "relative",
        bottom: active ? -1 : 0,
        cursor: "pointer",
        maxWidth: 200,
        minWidth: 0,
        flex: "0 0 auto",
        color: active ? T.text : T.textDim,
        fontSize: T.fs_label,
        fontFamily: T.sans,
        "&:hover": { bgcolor: active ? T.rail : T.panel },
      }}
    >
      {tab.is_pinned && (
        <PushPinIcon sx={{ fontSize: 12, color: T.textMute, transform: "rotate(45deg)" }} />
      )}
      {editing ? (
        <TextField
          inputRef={inputRef}
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          variant="standard"
          sx={{
            "& .MuiInputBase-input": { fontSize: T.fs_label, py: 0 },
            minWidth: 80,
          }}
        />
      ) : (
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {tab.title ?? "Untitled"}
        </Box>
      )}
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close conversation"
        sx={{
          width: 16,
          height: 16,
          border: "none",
          bgcolor: "transparent",
          cursor: "pointer",
          color: T.textMute,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: T.r_xs,
          "&:hover": { bgcolor: T.panel3, color: T.text },
        }}
      >
        <CloseIcon sx={{ fontSize: 12 }} />
      </Box>
    </Box>
  );
}

export function ConversationTabs() {
  const { activeAgentId, activeConvoId, setActiveConvoId, conversationTabs } = useChatRail();
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const create = useCreateConversation(activeAgentId);
  const close = useCloseConversation(activeAgentId);
  const rename = useRenameConversation(activeAgentId);
  const [popoverEl, setPopoverEl] = useState<HTMLElement | null>(null);

  const visibleBudget = T.conversation_tab_visible_budget;
  const { visible, overflow } = useMemo(
    () => computeVisibleOverflow(conversationTabs, activeConvoId, visibleBudget),
    [conversationTabs, activeConvoId, visibleBudget],
  );

  const handleCreate = async () => {
    const id = await create.mutateAsync();
    setActiveConvoId(id);
  };

  const handleClose = async (id: string) => {
    await close.mutateAsync(id);
    if (id === activeConvoId) {
      const next = conversationTabs.find((t) => t.id !== id);
      setActiveConvoId(next?.id ?? null);
    }
  };

  const handleRename = (id: string, title: string) => {
    rename.mutate({ conversation_id: id, title });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", height: "100%", gap: 0 }}>
      {visible.map((tab) => (
        <TabChrome
          key={tab.id}
          tab={tab}
          active={tab.id === activeConvoId}
          onClick={() => setActiveConvoId(tab.id)}
          onClose={() => handleClose(tab.id)}
          onRename={(title) => handleRename(tab.id, title)}
        />
      ))}

      {overflow.length > 0 && (
        <Tooltip title={`${overflow.length} more`} placement="bottom">
          <IconButton
            size="small"
            onClick={(e) => setPopoverEl(e.currentTarget)}
            aria-label="More conversations"
            sx={{ ml: 0.25, color: T.textMute }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="New conversation" placement="bottom">
        <IconButton
          size="small"
          onClick={handleCreate}
          disabled={create.isPending}
          aria-label="New conversation"
          sx={{ ml: 0.25, color: T.textMute }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(popoverEl)}
        anchorEl={popoverEl}
        onClose={() => setPopoverEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ minWidth: 240, py: 0.5 }}>
          {overflow.map((tab) => (
            <Box
              key={tab.id}
              onClick={() => {
                setActiveConvoId(tab.id);
                setPopoverEl(null);
              }}
              sx={{
                px: 1.5,
                py: 1,
                cursor: "pointer",
                fontSize: T.fs_body,
                fontFamily: T.sans,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": { bgcolor: T.panel },
              }}
            >
              {tab.is_pinned && (
                <PushPinIcon sx={{ fontSize: 12, color: T.textMute, transform: "rotate(45deg)" }} />
              )}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tab.title ?? "Untitled"}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose(tab.id);
                }}
                aria-label="Close conversation"
                sx={{
                  width: 16,
                  height: 16,
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  color: T.textMute,
                  "&:hover": { color: T.text },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Popover>
    </Box>
  );
}
